<template>
  <BaseCard v-if="internalOptions.length" class="overflow-hidden">
    <BaseCardBody clear-padding>
      <table class="table-auto min-w-full divide-y divide-slate-200">
        <tbody
          v-for="option in internalOptions"
          :key="option.handle"
          class="bg-white divide-y divide-slate-200"
        >
          <tr class="bg-slate-200">
            <th
              colspan="6"
              scope="colgroup"
              class="px-4 py-2 text-left text-sm font-semibold text-slate-900"
            >
              {{ option.display_name }}
            </th>
          </tr>
          <tr class="divide-x divide-slate-200 bg-slate-50">
            <th scope="col" class="w-2"></th>
            <th scope="col" class="px-3 py-2 text-right text-xs font-semibold text-slate-900 w-32">
              VALUE
            </th>
            <th scope="col" class="px-3 py-2 text-right text-xs font-semibold text-slate-900 w-32">
              PRICE / UNIT
            </th>
            <th scope="col" class="px-3 py-2 text-right text-xs font-semibold text-slate-900 w-32">
              FLAT PRICE
            </th>
            <th scope="col" class="px-3 py-2 text-right text-xs font-semibold text-slate-900 w-32">
              RESETTABLE PERIOD
            </th>
            <th scope="col" class="relative py-2 pl-3 pr-4 sm:pr-6">
              <span class="sr-only">Edit</span>
            </th>
          </tr>
          <template v-if="option.prices.length">
            <FeatureOptionPricingRow
              v-for="(price, priceIdx) in option.prices"
              :key="`${option.handle}_${priceIdx}`"
              :handle="option.handle"
              :value="price.value"
              :unit-amount="price.unit_amount"
              :flat-amount="price.flat_amount"
              :resettable-period="price.resettable_period"
              @update:value="price.value = $event"
              @update:unit-amount="price.unit_amount = $event"
              @update:flat-amount="price.flat_amount = $event"
              @update:resettable-period="price.resettable_period = $event"
              @remove="removePriceOption(option.handle, priceIdx)"
            />
          </template>
          <tr v-else class="bg-white divide-x divide-gray-200">
            <td class="w-2"></td>
            <td colspan="5" class="py-2 pl-3 pr-4 sm:pr-6 text-center text-sm">
              No price option is set for this feature option.
              <a
                role="button"
                class="text-blue-600 text-sm font-medium hover:text-blue-900 focus:outline-none focus:text-blue-800"
                @click="addPriceOption(option.handle)"
                >Add a price</a
              >
            </td>
          </tr>
          <tr v-if="option.prices.length" class="bg-white divide-x divide-gray-200">
            <td class="w-2"></td>
            <td colspan="5" class="py-2 pl-3 pr-4 sm:pr-6 text-right">
              <a
                role="button"
                class="text-blue-600 text-sm font-medium hover:text-blue-900 focus:outline-none focus:text-blue-800"
                @click="addPriceOption(option.handle)"
                >Add another price</a
              >
            </td>
          </tr>
        </tbody>
      </table>
    </BaseCardBody>
    <BaseCardBody v-if="validationErrors.length">
      <ul class="font-sm space-y-1">
        <li
          v-for="(validationError, validationErrorIdx) in validationErrors"
          :key="validationErrorIdx"
        >
          {{ validationError }}
        </li>
      </ul>
    </BaseCardBody>
    <BaseCardFooter>
      <div class="flex justify-end">
        <BaseButton
          :success="statusIsResolved"
          :danger="statusIsRejected"
          :primary="statusIsIdle || statusIsPending"
          :disabled="statusIsPending"
          @click="updateFeatureOptions"
        >
          {{ submitLabel }}
        </BaseButton>
      </div>
    </BaseCardFooter>
  </BaseCard>
</template>

<script>
import { pick } from 'lodash-es';
import { featureOptionsClient } from '@/api/clients/admin';
import FeatureOptionPricingRow from './FeatureOptionPricingRow.vue';

const STATUS_IDLE = 'idle';
const STATUS_PENDING = 'pending';
const STATUS_RESOLVED = 'resolved';
const STATUS_REJECTED = 'rejected';

export default {
  components: {
    FeatureOptionPricingRow,
  },
  props: {
    feature: {
      type: Object,
      required: true,
    },
  },
  data() {
    return {
      internalOptions: [],
      validationErrors: [],
      status: STATUS_IDLE,
    };
  },
  computed: {
    statusIsIdle() {
      return this.status === STATUS_IDLE;
    },
    statusIsPending() {
      return this.status === STATUS_PENDING;
    },
    statusIsResolved() {
      return this.status === STATUS_RESOLVED;
    },
    statusIsRejected() {
      return this.status === STATUS_REJECTED;
    },
    submitLabel() {
      switch (this.status) {
        case STATUS_PENDING:
          return 'Saving ...';
        case STATUS_RESOLVED:
          return 'Saved !';
        case STATUS_REJECTED:
          return 'Save failed !';
        default:
          return 'Save';
      }
    },
  },
  created() {
    this.internalOptions = JSON.parse(JSON.stringify(this.feature.options));
  },
  methods: {
    addPriceOption(handle) {
      const index = this.internalOptions.findIndex((option) => option.handle === handle);

      if (index === -1) {
        return;
      }

      this.internalOptions[index].prices.push({
        value: 1,
        unit_amount: 0,
        flat_amount: 0,
        resettable_period: 'lifetime',
      });
    },
    removePriceOption(handle, priceOptionIndex) {
      const optionIndex = this.internalOptions.findIndex((option) => option.handle === handle);

      if (
        optionIndex === -1 ||
        priceOptionIndex === -1 ||
        priceOptionIndex > this.internalOptions[optionIndex].prices.length - 1
      ) {
        return;
      }

      this.internalOptions[optionIndex].prices.splice(priceOptionIndex, 1);
    },
    updateFeatureOptions() {
      this.validationErrors = [];
      this.status = STATUS_PENDING;
      return featureOptionsClient
        .update(this.feature.handle, {
          options: this.internalOptions.map((option) => pick(option, ['handle', 'prices'])),
        })
        .then(() => {
          this.status = STATUS_RESOLVED;
        })
        .catch((error) => {
          if (error?.response?.status === 422) {
            this.validationErrors = Object.values(error.response?.data?.errors || []).flat();
          }

          this.status = STATUS_REJECTED;
        })
        .finally(() => {
          setTimeout(() => {
            this.status = STATUS_IDLE;
          }, 1000);
        });
    },
  },
};
</script>
